import { useMutation } from "@apollo/client";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { CameraCapturedPicture } from "expo-camera";
import { ImagePickerAsset } from "expo-image-picker";
import { InteractionManager } from "react-native";
import { CameraProvider } from "../../../../components/CameraViewFinder/components";
import {
  DrawerRootParamList,
  PhotoImageRootParamList,
  ProductImageDetailsRootParamList,
} from "../../../../types";
import { useGracefulBackgroundRemoval } from "../../../../utilities/background-eraser";
import { base64ToFile } from "../../../../utilities/base-64";
import { getBase64 } from "../../../../utilities/camera/utils";
import { useGlobalSettingsProvider } from "../../../../utilities/global-settings/hooks";
import { MUTATION_UPLOAD_PRODUCT_IMAGE } from "../../../../utilities/product/constants";
import { useSelectUploadImageProvider } from "../../components/SelectUploadImageProvider/hooks";
import { SelectUploadImageMethodScreenInner } from "../SelectUploadImageMethodScreenInner";
import { useEffect } from "react";
import { useProductImageDetailsRootScreenProvider } from "../../utilities/product-image-details-root";
import { useRoute } from "@react-navigation/native";
import { useFlashMessages } from "@towersystems/roam-common-native";

export type SelectUploadImageMethodScreenProps = NativeStackScreenProps<
  PhotoImageRootParamList &
    DrawerRootParamList &
    ProductImageDetailsRootParamList,
  "SelectUploadImageMethodScreen"
>;

export const SelectUploadImageMethodScreen = ({
  navigation,
}: SelectUploadImageMethodScreenProps) => {
  const route = useRoute<any>();

  const { sku } = route.params;

  const { showMessage } = useFlashMessages();

  const { automaticBackgroundRemoval } = useGlobalSettingsProvider();

  const { productId } = useProductImageDetailsRootScreenProvider();

  const { gracefulRemoveBackground } = useGracefulBackgroundRemoval();

  const [uploadImageMutation] = useMutation(MUTATION_UPLOAD_PRODUCT_IMAGE);

  const { handlers } = useSelectUploadImageProvider();

  async function handleFileUpload(file: File) {
    const res = await uploadImageMutation({
      variables: {
        input: {
          productId: productId,
          sendRetailer: true,
          file,
        },
      },
    });

    handlers.onUpload(navigation);

    InteractionManager.runAfterInteractions(() => {
      navigation.navigate("ImagesSentSuccessPartialScreen", {
        sku,
      });
    });
  }

  const savePhoto = async (
    photo: CameraCapturedPicture | ImagePickerAsset,
    removeBg?: boolean
  ) => {
    // file system URLs are not supported in the browser, falling back to uri on web provides base64 value
    const base64 = getBase64(photo);

    if (!base64 || !productId) return;

    let file = makeProductImageFile(base64, productId);

    if (automaticBackgroundRemoval || removeBg) {
      const maybeFile = await gracefulRemoveBackground(base64, productId, {
        preview: false,
      });
      if (!!maybeFile) {
        file = maybeFile;
      }
    }

    return handleFileUpload(file).catch((e) => {
      showMessage({
        message: "Failed to upload image",
        severity: "error",
      });
      throw e;
    });
  };

  return (
    <CameraProvider>
      <SelectUploadImageMethodScreenInner
        {...{ route, navigation, savePhoto }}
      />
    </CameraProvider>
  );
};

function makeProductImageFile(base64: string, productId: string): File {
  return base64ToFile(base64, `${productId}.jpg`);
}
